/**
 * Morning assignment — `09-equipment/equipment-assign.png`.
 *
 * The one equipment screen somebody opens every single day, at ten past six,
 * with crews standing in the yard. So it is built around what is *left* to
 * decide: the rows that answered themselves are ticked and quiet, and the two
 * or three that need a person are the only things with a button on them.
 *
 * Nothing here decides whether a piece is free — the exclusion constraint on
 * `visit_equipment_assignments` does, and the server computes these rows
 * from the same bookings it checks. A row that says "conflict" is exactly a
 * row the database would refuse.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MeResponse } from '@rowted/shared'
import { PageBar } from '../frame/page-bar.js'
import { Button, Card, Checkbox, EmptyState, SectionLabel, Tag } from '../components/primitives.js'
import { Toast } from '../components/overlays.js'
import { formatNumber, formatRate } from '../lib/format.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import type {
  AssignmentCandidate, CrewAssignment, EquipmentNeed, MorningAssignment, PoolRow,
} from '../lib/equipment-types.js'

/** The yard's day, which is what the timeline is drawn against. */
const DAY_START = 6
const DAY_END = 18

export function EquipmentAssignment({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const [day, setDay] = useState<0 | 1>(0)
  const [data, setData] = useState<MorningAssignment | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /*
   * Which auto rows this person has untied for today. Held here rather than
   * written through: unticking "the crew's own blowers" on a Tuesday is a
   * statement about Tuesday, not about the crew.
   */
  const [dropped, setDropped] = useState<Set<string>>(new Set())

  const date = useMemo(() => addDays(new Date().toISOString().slice(0, 10), day), [day])
  const canEdit = me.permissions.equipment === 'edit' || me.permissions.equipment === 'full'

  const load = useCallback(async () => {
    try {
      setData(await request<MorningAssignment>(`/equipment/assignment?date=${date}`))
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The day could not be loaded.')
    }
  }, [date])

  useEffect(() => { void load() }, [load])

  /** Books one piece onto one visit, and says plainly when it cannot. */
  const assign = useCallback(async (
    need: EquipmentNeed, equipmentId: string, from?: string | null,
  ) => {
    if (!need.visitId) {
      setNotice('That piece is not tied to a visit today, so there is nothing to book it on.')
      return
    }
    setBusy(true)
    try {
      await request('/equipment/assignment', {
        method: 'POST',
        body: { visitId: need.visitId, equipmentId, date, from: from ?? null },
      })
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be booked.')
    } finally {
      setBusy(false)
    }
  }, [date, load])

  const release = useCallback(async (assignmentId: string) => {
    setBusy(true)
    try {
      await request(`/equipment/assignment/${assignmentId}`, { method: 'DELETE' })
      await load()
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'That could not be undone.')
    } finally {
      setBusy(false)
    }
  }, [load])

  if (error) {
    return (
      <EmptyState title="The day could not be loaded" body={error}
        action={{ label: 'Try again', onClick: () => void load() }} />
    )
  }
  if (!data) return <p className="p-4 text-cell text-text-muted">Loading…</p>

  /** Every auto row still ticked — what "Send to crews" would book. */
  const pending = data.crews.flatMap((crew) => crew.needs
    .filter((need) => need.status === 'auto' && need.visitId && !dropped.has(need.key))
    .map((need) => ({ need, equipmentId: need.chosen[0]! })))

  async function sendToCrews() {
    setBusy(true)
    let booked = 0
    let refused = 0
    for (const { need, equipmentId } of pending) {
      try {
        await request('/equipment/assignment', {
          method: 'POST',
          body: { visitId: need.visitId, equipmentId, date },
        })
        booked += 1
      } catch {
        // One refusal is a row somebody has to look at, not a failed morning.
        refused += 1
      }
    }
    await load()
    setBusy(false)
    setNotice(refused === 0
      ? `Sent. ${formatNumber(booked)} ${booked === 1 ? 'piece is' : 'pieces are'} on the crews’ load sheets.`
      : `${formatNumber(booked)} booked, ${formatNumber(refused)} refused — somebody took them first. The rows are still here.`)
  }

  const { summary } = data
  const crewWord = data.crews.length === 1 ? 'crew' : 'crews'

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageBar
        title="Morning assignment"
        breadcrumbs={[{ label: 'Equipment', onClick: () => navigate('/equipment') }]}
        sub={`${formatNumber(data.crews.length)} ${crewWord} · ${formatNumber(summary.needs)} ${summary.needs === 1 ? 'need' : 'needs'}`}
        actions={canEdit
          ? (
            <Button
              variant="primary"
              disabled={busy || pending.length === 0}
              onClick={() => void sendToCrews()}
            >
              Send to crews
            </Button>
          )
          : undefined}
        tabs={[
          { key: 'today', label: `Today · ${shortDate(addDays(date, -day))}` },
          { key: 'tomorrow', label: `Tomorrow · ${shortDate(addDays(date, 1 - day))}` },
        ]}
        activeTab={day === 0 ? 'today' : 'tomorrow'}
        onTabChange={(key) => { setDropped(new Set()); setDay(key === 'today' ? 0 : 1) }}
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {summary.needs > 0 && (
          <Banner summary={summary} pending={pending.length} />
        )}

        {data.crews.length === 0
          ? (
            <EmptyState
              title="No crews are out on this day"
              body="Assignment is built from the day's visits. Schedule work on the crew board and the pieces each crew needs show up here."
              action={{ label: 'Crew board', onClick: () => navigate('/schedule') }}
            />
          )
          : (
            <div className="grid gap-4 xl:grid-cols-[1fr_minmax(320px,420px)]">
              <div className="flex flex-col gap-3">
                {data.crews.map((crew) => (
                  <CrewCard
                    key={crew.crewId}
                    crew={crew}
                    canEdit={canEdit}
                    busy={busy}
                    dropped={dropped}
                    onToggle={(key, on) => setDropped((current) => {
                      const next = new Set(current)
                      if (on) next.delete(key)
                      else next.add(key)
                      return next
                    })}
                    onAssign={(need, equipmentId, from) => void assign(need, equipmentId, from)}
                    onRelease={(assignmentId) => void release(assignmentId)}
                  />
                ))}
              </div>

              <Pool
                rows={data.pool}
                date={data.date}
                onOpen={(id) => navigate(`/equipment/${id}`)}
              />
            </div>
          )}
      </div>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </div>
  )
}

/** What answered itself, and what is left. */
function Banner({
  summary, pending,
}: {
  summary: MorningAssignment['summary']
  pending: number
}) {
  const settled = summary.auto
  const left = summary.choose + summary.conflicts

  return (
    <div className={cn('mb-3 rounded-card border p-3',
      left === 0 ? 'border-accent-line bg-accent-soft' : 'border-warn bg-warn-bg')}>
      <p className="m-0 text-cell text-text">
        <span className="font-semibold">
          {formatNumber(settled)} of {formatNumber(summary.needs)} settled on their own.
        </span>{' '}
        The pieces a crew always carries, and the trailers paired to them, came
        from today’s visits.
        {left > 0 && (
          <>
            {' '}
            <span className="font-semibold text-danger">
              {summary.choose > 0 && `${formatNumber(summary.choose)} to pick`}
              {summary.choose > 0 && summary.conflicts > 0 && ' · '}
              {summary.conflicts > 0 && `${formatNumber(summary.conflicts)} ${summary.conflicts === 1 ? 'conflict' : 'conflicts'}`}
              .
            </span>
          </>
        )}
      </p>
      {pending > 0 && (
        <p className="m-0 mt-1 text-sub text-text-muted">
          {formatNumber(pending)} {pending === 1 ? 'row is' : 'rows are'} ticked and
          not yet booked. “Send to crews” books them.
        </p>
      )}
    </div>
  )
}

function CrewCard({
  crew, canEdit, busy, dropped, onToggle, onAssign, onRelease,
}: {
  crew: CrewAssignment
  canEdit: boolean
  busy: boolean
  dropped: Set<string>
  onToggle: (key: string, on: boolean) => void
  onAssign: (need: EquipmentNeed, equipmentId: string, from?: string | null) => void
  onRelease: (assignmentId: string) => void
}) {
  const pieces = crew.needs.filter((need) => need.chosen.length > 0 && !dropped.has(need.key)).length

  return (
    <Card
      className="p-4"
      tone={crew.status === 'conflict' ? 'bad-edge'
        : crew.status === 'choose' ? 'warn-edge' : 'plain'}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span
          aria-hidden
          className="flex h-7 w-7 items-center justify-center rounded-full bg-surface-alt text-cell font-bold text-text"
        >
          {crew.crewName.slice(0, 1).toUpperCase()}
        </span>
        <span className="text-card font-semibold text-text">{crew.crewName}</span>
        <span className="text-sub text-text-muted">
          {formatNumber(crew.visitCount)} {crew.visitCount === 1 ? 'visit' : 'visits'}
          {crew.visits.length > 0
            && ` · ${crew.visits.map((visit) => visit.propertyName).join(', ')}`}
        </span>
        <span className="ml-auto">
          {crew.status === 'conflict'
            ? <Tag tone="bad">Conflict</Tag>
            : crew.status === 'choose'
              ? <Tag tone="warn">To pick</Tag>
              : (
                <Tag tone="ok">
                  Ready · {formatNumber(pieces)} {pieces === 1 ? 'piece' : 'pieces'}
                </Tag>
              )}
        </span>
      </div>

      <ul className="m-0 mt-3 list-none space-y-1.5 p-0">
        {crew.needs.map((need) => (
          <NeedRow
            key={need.key}
            need={need}
            canEdit={canEdit}
            busy={busy}
            on={!dropped.has(need.key)}
            onToggle={(on) => onToggle(need.key, on)}
            onAssign={onAssign}
            onRelease={onRelease}
          />
        ))}
      </ul>
    </Card>
  )
}

function NeedRow({
  need, canEdit, busy, on, onToggle, onAssign, onRelease,
}: {
  need: EquipmentNeed
  canEdit: boolean
  busy: boolean
  on: boolean
  onToggle: (on: boolean) => void
  onAssign: (need: EquipmentNeed, equipmentId: string, from?: string | null) => void
  onRelease: (assignmentId: string) => void
}) {
  const named = need.candidates[0]
  const alternates = need.candidates.slice(1)
  const settled = need.status === 'auto' || need.status === 'assigned'
  /*
   * The row is named after what the service asked for, but somebody may have
   * answered it with another of the same type. Saying which one went is the
   * whole point of the row once it is settled.
   */
  const stoodIn = need.chosen[0] && need.chosen[0] !== need.equipmentId
    ? need.candidates.find((candidate) => candidate.equipmentId === need.chosen[0])
    : undefined

  return (
    <li className={cn('flex flex-wrap items-start gap-2 rounded-card border px-2.5 py-2',
      need.status === 'conflict' ? 'border-danger bg-danger-bg'
        : need.status === 'choose' ? 'border-warn bg-warn-bg'
          : 'border-line bg-surface')}>
      {settled
        ? (
          <Checkbox
            checked={on}
            disabled={!canEdit || need.status === 'assigned'}
            onChange={(event) => onToggle(event.currentTarget.checked)}
            aria-label={`Take ${need.label}`}
            className="mt-0.5"
          />
        )
        : <span aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />}

      <span className="min-w-[200px] flex-1">
        <span className="block text-cell text-text">
          <span className={cn('font-semibold', !on && 'line-through text-text-faint')}>
            {need.label}
          </span>
          {need.qty > 1 && <span className="text-text-muted"> · ×{formatNumber(need.qty)}</span>}
        </span>
        <span className="block text-sub text-text-muted">
          {need.detail}
          {stoodIn
            ? ` · ${stoodIn.name} is going instead`
            : (
              <>
                {named?.orgUnitName && ` · ${named.orgUnitName}`}
                {named?.unavailable && ` · ${named.unavailable}`}
                {named?.freeFrom && !named.unavailable
                  && ` · free from ${clock(named.freeFrom)}`}
              </>
            )}
        </span>
      </span>

      <span className="flex shrink-0 flex-wrap items-center gap-1.5">
        {need.status === 'choose' && alternates.map((candidate) => (
          <Button
            key={candidate.equipmentId}
            disabled={!canEdit || busy}
            onClick={() => onAssign(need, candidate.equipmentId)}
          >
            Take {shortName(candidate)}
          </Button>
        ))}

        {need.status !== 'assigned' && named?.freeFrom && !named.unavailable && (
          <Button
            disabled={!canEdit || busy}
            onClick={() => onAssign(need, named.equipmentId, named.freeFrom)}
          >
            Use after {clock(named.freeFrom)}
          </Button>
        )}

        {need.rental && !settled && (
          <Tag tone="quiet">
            Hire in · {need.rental.name} · {formatRate(Number(need.rental.price))}/{need.rental.unit}
          </Tag>
        )}

        {need.status === 'assigned' && named?.assignmentId && (
          <>
            <Tag tone="ok">Assigned</Tag>
            <Button
              variant="ghost"
              disabled={!canEdit || busy}
              onClick={() => onRelease(named.assignmentId!)}
            >
              Give it back
            </Button>
          </>
        )}

        {need.status === 'auto'
          && <Tag tone="ok">Auto</Tag>}
        {need.status === 'choose' && <Tag tone="warn">Choose</Tag>}
        {need.status === 'conflict'
          && <Tag tone="bad">Conflict</Tag>}
      </span>
    </li>
  )
}

/** The day as a strip per piece: who has it, and for which hours. */
function Pool({
  rows, date, onOpen,
}: {
  rows: PoolRow[]
  date: string
  onOpen: (id: string) => void
}) {
  const hours = []
  for (let hour = DAY_START; hour <= DAY_END; hour += 2) hours.push(hour)

  /*
   * The strip's own ends, as instants. Measuring a booking by its hour of day
   * instead put an all-day booking — midnight to midnight — at nought o'clock
   * twice, so the whole day drew as a four-percent sliver at the left edge.
   */
  const startsAt = new Date(`${date}T00:00:00`).setHours(DAY_START, 0, 0, 0)
  const endsAt = new Date(`${date}T00:00:00`).setHours(DAY_END, 0, 0, 0)
  const fraction = (iso: string): number =>
    Math.min(1, Math.max(0, (new Date(iso).getTime() - startsAt) / (endsAt - startsAt)))

  return (
    <Card className="h-fit p-4">
      <div className="mb-2 flex items-baseline justify-between">
        <SectionLabel>Today’s pool</SectionLabel>
        <span className="text-sub text-text-muted">6 am → 6 pm</span>
      </div>

      <div className="grid grid-cols-[minmax(110px,1fr)_2fr] items-center gap-x-2 text-sub text-text-faint">
        <span />
        <span className="flex justify-between">
          {hours.map((hour) => <span key={hour}>{hour > 12 ? hour - 12 : hour}</span>)}
        </span>
      </div>

      <ul className="m-0 mt-1 list-none space-y-1 p-0">
        {rows.map((row) => (
          <li
            key={row.equipmentId}
            className="grid grid-cols-[minmax(110px,1fr)_2fr] items-center gap-x-2"
          >
            <button
              type="button"
              onClick={() => onOpen(row.equipmentId)}
              className={cn('truncate text-left text-sub hover:underline',
                row.status === 'available' ? 'text-text' : 'text-text-faint')}
            >
              {row.name}
              {row.status !== 'available' && ` · ${row.status.replace(/_/g, ' ')}`}
            </button>

            <span className="relative block h-4 rounded-[4px] bg-surface-alt">
              {row.bookings.map((booking) => {
                const left = fraction(booking.from)
                const right = fraction(booking.to)
                return (
                  <span
                    key={`${booking.from}-${booking.to}-${booking.reason}`}
                    title={`${booking.crewName ?? booking.reason} · ${clock(booking.from)}–${clock(booking.to)}`}
                    className={cn('absolute top-0 h-4 overflow-hidden whitespace-nowrap',
                      'rounded-[4px] px-1 text-[10px] leading-4 text-white',
                      booking.reason === 'visit' ? 'bg-accent' : 'bg-danger')}
                    style={{
                      left: `${left * 100}%`,
                      width: `${Math.max(right - left, 0.04) * 100}%`,
                    }}
                  >
                    {booking.crewName ?? booking.reason}
                  </span>
                )
              })}
            </span>
          </li>
        ))}
      </ul>

      {rows.length === 0 && (
        <p className="m-0 text-sub text-text-muted">Nothing in this branch’s yard yet.</p>
      )}

      <p className="mb-0 mt-3 text-sub text-text-muted">
        A piece can only be on one visit at a time. Booking it where it is
        already taken shows up here as a conflict rather than going through —
        the database refuses it, so the load sheet and the yard cannot disagree.
      </p>
    </Card>
  )
}

const shortName = (candidate: AssignmentCandidate): string =>
  candidate.assetTag || candidate.name

const clock = (iso: string): string =>
  new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase().replace(':00', '')

const shortDate = (iso: string): string =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
  })

function addDays(iso: string, days: number): string {
  const at = new Date(`${iso}T00:00:00Z`)
  at.setUTCDate(at.getUTCDate() + days)
  return at.toISOString().slice(0, 10)
}
